import React, { useState } from 'react';
import { Card, Input, Row, Col, Space, Select, Button } from 'antd';
import { AccountBookOutlined, SearchOutlined, PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { DataTable, MoneyCell, PageLayout, StatusCell } from '../../ui';
import dayjs from 'dayjs';
import { useGetReceivablesListQuery, ReceivableListItem } from '../../store/api/financeApi';
import { CreateApplicationClientPaymentModal } from '../../components/finance';

const { Option } = Select;

function getReceivableStatus(row: ReceivableListItem): string {
  const { remaining, amount, dueDate } = row;
  if (remaining === 0) return 'PAID';
  if (!dueDate) return remaining > 0 && remaining < amount ? 'PARTIALLY_PAID' : 'NOT_PAID';
  const today = dayjs().startOf('day');
  const due = dayjs(dueDate).startOf('day');
  if (!due.isValid()) return remaining > 0 && remaining < amount ? 'PARTIALLY_PAID' : 'NOT_PAID';
  if (remaining > 0 && due.isBefore(today)) return 'OVERDUE';
  if (remaining > 0 && remaining < amount) return 'PARTIALLY_PAID';
  return 'NOT_PAID';
}

function getDisplayStatus(row: ReceivableListItem): string {
  const s = row.obligationStatus;
  if (s && ['PAID', 'PARTIALLY_PAID', 'NOT_PAID', 'OVERDUE'].includes(s)) return s;
  return getReceivableStatus(row);
}

const FinanceReceivablesPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [search, setSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [payAppId, setPayAppId] = useState<number | null>(null);
  const [payAppLabel, setPayAppLabel] = useState<string>('');

  const { data, isLoading, refetch } = useGetReceivablesListQuery({
    page,
    limit,
    search: search.trim() || undefined,
    status: statusFilter || undefined
  });

  const receivables = data?.receivables ?? [];
  const pagination = data?.pagination ?? { page: 1, limit: 20, total: 0 };

  const columns: ColumnsType<ReceivableListItem> = [
    {
      title: 'Контрагент',
      key: 'counterparty',
      ellipsis: true,
      render: (_, r) => r.application?.client?.name ?? '—'
    },
    {
      title: 'Заявка',
      key: 'application',
      width: 120,
      render: (_, r) => (r.application?.internalNumber ? `#${r.application.internalNumber}` : '—')
    },
    {
      title: 'Маршрут',
      key: 'route',
      width: 90,
      render: (_, r) => (r.route ? `#${r.route.id}` : '—')
    },
    {
      title: 'Сумма',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 120,
      align: 'right',
      render: (_: unknown, r: ReceivableListItem) =>
        <MoneyCell value={Number(r.totalAmount ?? r.amount ?? 0)} type="income" />
    },
    {
      title: 'Оплачено',
      dataIndex: 'paidAmount',
      key: 'paidAmount',
      width: 120,
      align: 'right',
      render: (_: unknown, r: ReceivableListItem) =>
        <MoneyCell value={Number(r.paidAmount ?? r.paid ?? 0)} type="income" />
    },
    {
      title: 'Остаток',
      dataIndex: 'remainingAmount',
      key: 'remainingAmount',
      width: 120,
      align: 'right',
      render: (_: unknown, r: ReceivableListItem) =>
        <MoneyCell value={Number(r.remainingAmount ?? r.remaining ?? 0)} type="income" />
    },
    {
      title: 'Срок оплаты',
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 120,
      render: (d: string) => (d ? dayjs(d).format('DD.MM.YYYY') : '—')
    },
    {
      title: 'Статус',
      key: 'statusDisplay',
      width: 140,
      render: (_, r) => {
        const status = getDisplayStatus(r);
        return <StatusCell status={status} />;
      }
    },
    {
      title: '',
      key: 'pay',
      width: 56,
      fixed: 'right',
      render: (_, r) =>
        r.applicationId ? (
          <Button
            type="link"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => {
              setPayAppId(r.applicationId!);
              setPayAppLabel(
                r.application?.internalNumber
                  ? `#${r.application.internalNumber} — ${r.application?.client?.name ?? ''}`
                  : `Заявка #${r.applicationId}`
              );
            }}
          >
            Оплата
          </Button>
        ) : null
    }
  ];

  return (
    <PageLayout
      title={
        <Space>
          <AccountBookOutlined />
          <span>Дебиторская задолженность</span>
        </Space>
      }
      subtitle="Остатки к получению от клиентов по обязательствам"
    >
      <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col>
            <Input
              placeholder="Поиск по контрагенту"
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              style={{ width: 220 }}
            />
          </Col>
          <Col>
            <Select
              placeholder="Статус"
              allowClear
              value={statusFilter ?? undefined}
              onChange={setStatusFilter}
              style={{ width: 180 }}
            >
              <Option value="NOT_PAID">Не оплачено</Option>
              <Option value="PARTIALLY_PAID">Частично</Option>
              <Option value="PAID">Оплачено</Option>
              <Option value="OVERDUE">Просрочено</Option>
            </Select>
          </Col>
        </Row>

        <DataTable<ReceivableListItem>
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={receivables}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: false,
            showTotal: (t) => `Всего: ${t}`,
            onChange: (p) => setPage(p)
          }}
          scroll={{ x: 'max-content' }}
        />

        <CreateApplicationClientPaymentModal
          open={payAppId != null}
          applicationId={payAppId}
          applicationLabel={payAppLabel}
          onCancel={() => setPayAppId(null)}
          onSuccess={() => void refetch()}
        />
      </Card>
    </PageLayout>
  );
};

export default FinanceReceivablesPage;
